/// <reference lib="dom" />
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const lateinit = null as any;

export function unreachable(value: never, template: string = 'Unhandled case: %'): never {
	if (value !== undefined) {
		throw new Error(template.replace('%', value).trim());
	} else {
		throw new Error(template.replace('%', '').trim());
	}
}

export function softUnreachable(value: never, template: string = 'Unhandled case: %') {
	if (value !== undefined) {
		console.error(template.replace('%', value).trim());
	} else {
		console.error(template.replace('%', '').trim());
	}
}

export function delayMs(delay: number): Promise<void> {
	return new Promise((resolve, reject) => {
		setTimeout(() => {
			resolve();
		}, delay);
	});
}

export function toTitleCase(str: string) {
	return str
		.split(' ')
		.map((word) => word[0].toUpperCase() + word.slice(1))
		.join(' ');
}

export function toSentenceCase(str: string) {
	return str[0].toUpperCase() + str.slice(1);
}

export type JSONValue = string | number | boolean | null | JSONObject | JSONArray;

export interface JSONObject {
	[key: string]: JSONValue;
}

export interface JSONArray extends Array<JSONValue> {}

export const Random = {
	range(from = 0, to = 1) {
		return from + Math.random() * (to - from);
	},

	rangeInt(from: number, to: number) {
		return Math.floor(Random.range(from, to));
	},

	fromString(s: string, count = 1) {
		let acc = '';

		for (let i = 0; i < count; i++) {
			const index = Random.rangeInt(0, s.length);
			const c = s[index];
			acc += c;
		}
		return acc;
	},

	fromArray<T>(arr: T[]): T {
		const i = Random.rangeInt(0, arr.length);
		return arr[i] as T;
	},

	fromTuple<const T extends readonly unknown[]>(arr: T): T[number] {
		const i = Random.rangeInt(0, arr.length);
		return arr[i] as T[number];
	},

	chance(threshold: number) {
		return Math.random() < threshold;
	},

	direction(lenScale = 1) {
		const x = Random.range(-1, 1);
		const y = Random.range(-1, 1);
		const len = Math.sqrt(x * x + y * y);

		return { x: (x / len) * lenScale, y: (y / len) * lenScale };
	},
};

export function createRenderLoop(onUpdate: (dt: number) => boolean): Promise<void> {
	return new Promise((resolve, reject) => {
		requestAnimationFrame((prevTime) => {
			prevTime /= 1000;
			function update(time: number) {
				time /= 1000;
				const delta = time - prevTime;
				if (onUpdate(delta)) {
					prevTime = time;
					requestAnimationFrame(update);
				} else {
					resolve();
				}
			}
			requestAnimationFrame(update);
		});
	});
}

export function splitName(
	fullName: string,
	domain: string | undefined = undefined,
): { firstName: string; lastName: string; email: string } {
	// Split the full name into an array of strings
	const nameParts = fullName.split(' ');
	if (nameParts.length === 1) {
		const email = fullName.toLowerCase() + '@' + (domain || 'example.com');

		return {
			firstName: fullName,
			lastName: '',
			email: email,
		};
	}

	// Check if the last part is a common prefix or particle
	const particles = [
		'de',
		'di',
		'do',
		'der',
		'den',
		'del',
		'des',
		'dal',
		'du',
		'la',
		'le',
		'los',
		'les',
		'van',
		'op',
		'ten',
		'ter',
		'het',
	];
	let lastNameIndex = nameParts.length - 1;
	while (lastNameIndex > 0 && particles.includes((nameParts[lastNameIndex - 1] || '').toLowerCase())) {
		lastNameIndex--;
	}
	const lastName = nameParts.splice(lastNameIndex).join(' ');
	const firstName = nameParts.join(' ');
	const email = (firstName[0]?.toLowerCase() || 'x') + '.' + lastName.toLowerCase().replace(/\s/g, '') + '@' + (domain || 'example.com');

	return {
		firstName: firstName,
		lastName: lastName,
		email: email,
	};
}

/**
 * Creates a debounced function that delays invoking the provided function until
 * after the specified duration has elapsed since the last time the debounced function was invoked.
 *
 * @template T - The type of the function to debounce.
 * @param {T} fn - The function to debounce.
 * @param {number} durationMS - The number of milliseconds to delay invocation.
 * @returns {(...args: Parameters<T>) => void} A new debounced function that delays invoking `fn`.
 *
 * @example
 * const logMessage = (message: string) => {
 *   console.log(message);
 * };
 *
 * const debouncedLogMessage = debounce(logMessage, 1000);
 *
 * debouncedLogMessage('Hello, World!'); // Will log 'Hello, World!' to the console after 1 second if not called again within that time.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounce<T extends (...args: any[]) => void>(fn: T, durationMS: number): (...args: Parameters<T>) => void {
	let timeoutId: ReturnType<typeof setTimeout> | null = null;

	return function (...args: Parameters<T>): void {
		if (timeoutId !== null) {
			clearTimeout(timeoutId);
		}

		timeoutId = setTimeout(() => {
			fn(...args);
		}, durationMS);
	};
}

export function downloadString(content: string, filename: string, contentType: string) {
	const blob = new Blob([content], { type: contentType });
	const link = document.createElement('a');
	link.href = URL.createObjectURL(blob);
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

export function generateUUID(): string {
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
		const random = (Math.random() * 16) | 0;
		const value = char === 'x' ? random : (random & 0x3) | 0x8;
		return value.toString(16);
	});
}

function toBase62(num: number): string {
	const chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
	let result = '';

	while (num > 0) {
		const remainder = num % 62;
		result = chars[remainder] + result;
		num = Math.floor(num / 62);
	}

	return result || '0'; // Return "0" if num was originally 0
}

function fromBase62(str: string): number {
	const chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
	const base = 62;
	let num = 0;

	for (let i = 0; i < str.length; i++) {
		const char = str[i];
		const value = chars.indexOf(char);
		if (value === -1) {
			throw new Error(`Invalid character "${char}" in base-62 string`);
		}
		num = num * base + value;
	}

	return num;
}

/**
 * converts a numeric id to a QR URL
 * @param id the id to encode
 */
export function idToQr(id: number | string) {
	const stringId = typeof id === 'string' ? id : toBase62(id);
	return stringId;
}

/**
 * converts a qr code URL into a numeric database id
 * @param qrUrl the QR url to decode
 * @returns the numeric database id or -1 if failed
 */
export function qrToCode(qrUrl: string) {
	const urlParts = qrUrl.split('/');
	return urlParts[urlParts.length - 1] || '';
}

export function qrCodeToId(qrCode: string) {
	try {
		return fromBase62(qrCode);
	} catch (e) {
		// input was invalid so atob threw
		return -1;
	}
}

type Tuple<T, N extends number, R extends T[] = []> = R['length'] extends N ? R : Tuple<T, N, [...R, T]>;

export function repeat<T, N extends number>(count: N, value: T): Tuple<T, N> {
	return Array(count).fill(value) as Tuple<T, N>;
}

export function prefersReducedMotion() {
	return globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function onUnmount(it: HTMLElement, cb: () => void) {
	const observer = new MutationObserver((mutations, obs) => {
		if (!document.body.contains(it)) {
			cb();
			obs.disconnect();
		}
	});

	observer.observe(document.body, { childList: true, subtree: true });
}

export function shuffle<T>(arr: T[]) {
	return arr.sort(() => Math.random() * 2 - 1);
}

export function deepClone<T extends JSONValue>(v: T): T {
	return JSON.parse(JSON.stringify(v));
}

export function clamp(v: number, a: number, b: number) {
	let min: number;
	let max: number;
	if (a > b) {
		max = a;
		min = b;
	} else {
		min = a;
		max = b;
	}
	return Math.min(max, Math.max(v, min));
}

export function lerpUnclamped(a: number, b: number, t: number): number {
	return (1 - t) * a + t * b;
}

export function lerp(a: number, b: number, t: number): number {
	return lerpUnclamped(a, b, clamp(t, 0, 1));
}

export function invLerpUnclamped(p: number, a: number, b: number) {
	const v = (p - a) / (b - a);
	return isNaN(v) ? 0 : v;
}

export function invLerp(p: number, a: number, b: number) {
	return invLerpUnclamped(clamp(p, a, b), a, b);
}
